import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, FileText, RotateCcw } from 'lucide-react';
import { apiCall } from '@/lib/api';
import { SignaturePad, type SignaturePadHandle } from '@/components/SignaturePad';

interface ActiveWaiver {
  id: string;
  title: string;
  body_md: string;
  version: number;
  created_at: string;
}

type MemberHit = { id: string; name: string };

// Very tiny markdown → HTML for the waiver body. We don't pull a full
// markdown lib for this single use; just newlines + simple bold/italic +
// '#'/'##' headings is enough for a typical liability paragraph.
function renderMarkdown(md: string): string {
  return md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-semibold mt-6 mb-2">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-6 mb-3">$1</h1>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\n\n+/g, '</p><p class="mt-3">')
    .replace(/\n/g, '<br/>')
    .replace(/^/, '<p>')
    .concat('</p>');
}

export default function SignWaiver() {
  const [waiver, setWaiver] = useState<ActiveWaiver | null>(null);
  const [loadingWaiver, setLoadingWaiver] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [hits, setHits] = useState<MemberHit[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const padRef = useRef<SignaturePadHandle>(null);

  useEffect(() => {
    (async () => {
      try {
        const w = await apiCall<ActiveWaiver>('/v1/waivers/active');
        setWaiver(w);
      } catch (e) {
        if (e instanceof Error && e.message.includes('404')) setErr('No active waiver is configured.');
        else setErr(e instanceof Error ? e.message : 'Failed');
      } finally { setLoadingWaiver(false); }
    })();
  }, []);

  // Debounced member search to link the signature to an existing member
  useEffect(() => {
    if (memberId) return;
    const q = name.trim();
    if (q.length < 2) { setHits([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const r = await apiCall<{ items: MemberHit[] }>('/v1/check_ins/member-search', { query: { q } });
        if (active) setHits(r.items);
      } catch { /* ignore */ }
    }, 200);
    return () => { active = false; clearTimeout(t); };
  }, [name, memberId]);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waiver) return;
    if (!name.trim()) { setErr('Please type your full name.'); return; }
    const sig = padRef.current?.toDataURL();
    if (!sig) { setErr('Please draw your signature in the box.'); return; }
    setSubmitting(true); setErr(null);
    try {
      await apiCall('/v1/waivers/sign', {
        method: 'POST',
        body: {
          waiver_id: waiver.id,
          member_id: memberId,
          signer_name: name.trim(),
          signer_email: email.trim() || null,
          signature_data_url: sig,
        },
      });
      setDone(true);
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setSubmitting(false); }
  }, [waiver, name, email, memberId]);

  if (loadingWaiver) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (err && !waiver) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <FileText className="mx-auto h-10 w-10 text-gray-400" />
          <p className="mt-4 text-gray-700">{err}</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-md w-full bg-white border-2 border-green-300 rounded-2xl p-10 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-16 w-16 text-green-600" />
          <h2 className="mt-4 text-2xl font-semibold text-green-900">Thanks, {name.trim().split(' ')[0]}.</h2>
          <p className="text-green-700 mt-2">Your waiver has been recorded. You're all set.</p>
        </div>
      </div>
    );
  }
  if (!waiver) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white py-8 px-4">
      <div className="max-w-3xl mx-auto bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h1 className="text-2xl font-bold">{waiver.title}</h1>
          <p className="text-xs text-gray-500">United Wrestling Club · waiver v{waiver.version}</p>
        </div>
        <div className="px-6 py-6 prose max-w-none text-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(waiver.body_md) }} />

        <form onSubmit={submit} className="border-t px-6 py-6 space-y-4 bg-gray-50">
          <h2 className="text-lg font-semibold">Sign below</h2>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Full name</label>
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setMemberId(null); }}
              required
              className="w-full rounded-md border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Type your full name"
            />
            {!memberId && hits.length > 0 && (
              <ul className="mt-1 border rounded-md bg-white shadow-sm divide-y">
                {hits.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => { setName(m.name); setMemberId(m.id); setHits([]); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50"
                    >
                      {m.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {memberId && <p className="text-xs text-green-700 mt-1">Linked to your member record.</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email (optional)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Signature</label>
            <SignaturePad ref={padRef} width={600} height={180} />
            <button
              type="button"
              onClick={() => padRef.current?.clear()}
              className="mt-1 text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" /> Clear
            </button>
          </div>

          {err && <p className="text-sm text-red-600">{err}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-indigo-600 text-white rounded-md py-3 text-base font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'I agree and sign'}
          </button>
        </form>
      </div>
    </div>
  );
}
